import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styles from './category-row.module.scss';
import listStyles from './list-components.module.scss';
import {
  RenderCategoryIcon,
  RenderContentType,
  RenderTags,
  RenderMetadata,
  RenderLabels,
  RenderIndicationTag,
} from '../components';
import { CATEGORY_TYPES, ROUTES } from 'shared/constants';
import classnames from 'classnames';

export function CategoryRowComponent({
  title = '',
  type,
  tags,
  labels,
  metadata,
  options,
  lastUpdatedAt,
  categoryType,
  adminMode,
  onBlur,
  onClick,
  hasEditPermission,
  actions,
  badge,
  wrapperClass,
  renderItemOptions = {},
}) {
  const location = useLocation();

  const hideOnCurrentPage =
    location.pathname === ROUTES.ROTATIONS || location.pathname === ROUTES.USER_PROFILE;

  const adjustedRenderOptions = {
    ...renderItemOptions,
    showMetadata: hideOnCurrentPage ? false : renderItemOptions.showMetadata,
    showLabels: hideOnCurrentPage ? false : renderItemOptions.showLabels,
    showTags: hideOnCurrentPage ? false : renderItemOptions.showTags,
    showContentType: hideOnCurrentPage ? false : (renderItemOptions.showContentType ?? true),
  };

  const isCategory = type === CATEGORY_TYPES.CATEGORY;
  const [titleInput, setTitleInput] = useState(title);
  const showUpdatedIndication = !adminMode && lastUpdatedAt;

  useEffect(() => {
    if (titleInput !== title) {
      setTitleInput(title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, setTitleInput]);

  const onInputFocus = useCallback(
    e => {
      // Only intercept the click when the title is editable; otherwise let it
      // bubble to the row so a read-only category still opens on click.
      if (!(adminMode && hasEditPermission)) return;
      e.stopPropagation();
    },
    [adminMode, hasEditPermission],
  );

  const onHeaderPress = useCallback(e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.target.blur();
    }
  }, []);

  const onChange = useCallback(
    e => {
      const { value } = e.target;
      setTitleInput(value);
    },
    [setTitleInput],
  );

  return (
    <button
      type="button"
      className={classnames([styles.root, wrapperClass])}
      onClick={onClick}
      aria-label={title}>
      <div className={styles.titleContainer}>
        <RenderCategoryIcon
          title={title}
          type={type}
          isCategory={isCategory}
          categoryType={categoryType}
          adminMode={adminMode}
          options={options}
          styles={listStyles}
        />
        <div className={styles.titleWrapper}>
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <h5
            className={styles.text}
            contentEditable={adminMode && hasEditPermission}
            suppressContentEditableWarning={true}
            onBlur={onBlur}
            onKeyPress={onHeaderPress}
            onChange={onChange}
            onClick={onInputFocus}
            role={adminMode && hasEditPermission ? 'textbox' : undefined}
            tabIndex={adminMode && hasEditPermission ? 0 : undefined}>
            {titleInput}
          </h5>
        </div>
        {badge}
        {adjustedRenderOptions.showContentType && (
          <RenderContentType tags={tags} styles={listStyles} />
        )}
        {showUpdatedIndication && <RenderIndicationTag lastUpdate={lastUpdatedAt} />}
      </div>
      <div className={styles.tags}>
        {labels?.length > 0 && adjustedRenderOptions.showLabels && (
          <RenderLabels labels={labels} styles={listStyles} />
        )}
        {adjustedRenderOptions.showMetadata && (
          <RenderMetadata
            metadata={metadata}
            adminMode={adminMode}
            isCategory={isCategory}
            categoryType={categoryType}
            options={options}
            styles={listStyles}
          />
        )}
        {adjustedRenderOptions.showTags && <RenderTags tags={tags} styles={listStyles} />}
      </div>
      <div className={styles.actions}>{actions}</div>
    </button>
  );
}
